import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { useInboundEmails } from '@/api/rpc/hooks/communications/useInboundEmails';
import { getInboundEmailReceivedAt } from '@/types/application/InboundEmailDTO';
import { useDebouncedState } from '@react-hookz/web';
import { T, useGT } from 'gt-react';

interface EmailSearchComboboxProps {
  onSelect: (emailId: string) => void;
  onCancel: () => void;
}

export function EmailSearchCombobox({
  onSelect,
  onCancel,
}: EmailSearchComboboxProps) {
  const gt = useGT();
  const [debouncedSearch, setDebouncedSearch] = useDebouncedState('', 300);

  const { data, isLoading } = useInboundEmails(
    debouncedSearch.length >= 2
      ? { search: debouncedSearch, pageSize: 10 }
      : { pageSize: 10 },
  );

  const emails = data?.edges ?? [];

  return (
    <Popover open onOpenChange={(open) => !open && onCancel()}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline">
          <T context="email search combobox trigger button">Search emails...</T>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={gt('Search by subject, sender...', {
              context: 'email search combobox input placeholder',
            })}
            value={debouncedSearch}
            onValueChange={setDebouncedSearch}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? gt('Searching...', {
                    context: 'email search combobox loading state',
                  })
                : gt('No emails found', {
                    context: 'email search combobox empty state',
                  })}
            </CommandEmpty>
            <CommandGroup>
              {emails.map((email) => (
                <CommandItem
                  key={email.id}
                  value={email.id}
                  onSelect={() => onSelect(email.id)}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium text-sm truncate">
                      {email.subject ??
                        gt('(No subject)', {
                          context: 'email search combobox item with no subject',
                        })}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {email.from ??
                        gt('Unknown sender', {
                          context: 'email search combobox item, unknown sender',
                        })}{' '}
                      &middot;{' '}
                      {getInboundEmailReceivedAt(email).toLocaleDateString()}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
